import React, { useState } from 'react';
import { KatButton, KatRow, KatColumn } from '@amzn/katal-react';
import { TranslateService, TranslateJobParams } from '../../services/Translate/TranslateService';
import { TARGET_FIELDS_BY_RESOURCE_TYPE, DEFAULT_TARGET_FIELDS } from './translateFields';
import FieldMultiSelect from './FieldMultiSelect';
import './TranslatePage.scss';

interface TranslatePageProps {
    stage: string;
    realm: string;
}

const RESOURCE_TYPES = ['Refund', 'PaymentMethodRefund', 'Charge', 'PaymentMethodCharge'];

const QUERY_TYPES = [
    { value: 'multi_match', label: 'Multi-match (search across all fields)' },
    { value: 'term', label: 'Term (exact match on source field)' },
];

const MAX_DISPLAY_ROWS = 100;

type JobStatus = 'idle' | 'uploading' | 'processing' | 'succeeded' | 'failed';

const TranslatePage: React.FC<TranslatePageProps> = ({ stage, realm }) => {
    const [identifiers, setIdentifiers] = useState('');
    const [resourceType, setResourceType] = useState('Refund');
    const [targetFields, setTargetFields] = useState<string[]>(DEFAULT_TARGET_FIELDS);
    const [queryType, setQueryType] = useState('multi_match');
    const [sourceField, setSourceField] = useState('resource_id');

    const availableFields = TARGET_FIELDS_BY_RESOURCE_TYPE[resourceType] || [];

    const [jobStatus, setJobStatus] = useState<JobStatus>('idle');
    const [results, setResults] = useState<string[][]>([]);
    const [headers, setHeaders] = useState<string[]>([]);
    const [truncated, setTruncated] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [downloadUrl, setDownloadUrl] = useState('');
    const [error, setError] = useState('');

    const handleResourceTypeChange = (newType: string) => {
        setResourceType(newType);
        setTargetFields(DEFAULT_TARGET_FIELDS);
        setSourceField('resource_id');
    };

    const handleSubmit = async () => {
        const lines = identifiers.trim().split('\n').filter(l => l.trim());
        if (lines.length === 0) {
            setError('Please enter at least one identifier');
            return;
        }
        if (targetFields.length === 0) {
            setError('Please select at least one target field');
            return;
        }

        setError('');
        setResults([]);
        setJobStatus('uploading');

        try {
            const csv = 'value\n' + lines.map(l => l.trim()).join('\n');

            const presignedUrl = await TranslateService.getPresignedUrl(stage, realm);
            const fileName = TranslateService.extractFileName(presignedUrl);

            await TranslateService.uploadCsv(presignedUrl, csv);

            setJobStatus('processing');

            const params: TranslateJobParams = {
                sourceField,
                resourceType,
                targetFields: targetFields.join(','),
                queryType,
            };
            const jobId = await TranslateService.submitJob(stage, realm, fileName, params);

            const result = await TranslateService.pollUntilComplete(stage, realm, jobId);

            if (result.status === 'SUCCEEDED' && result.downloadUrl) {
                const csvData = await TranslateService.downloadResult(result.downloadUrl);
                const parsed = parseCsv(csvData);

                if (parsed.length > 1) {
                    setHeaders(parsed[0]);
                    const dataRows = parsed.slice(1);
                    if (dataRows.length > MAX_DISPLAY_ROWS) {
                        setResults(dataRows.slice(0, MAX_DISPLAY_ROWS));
                        setTruncated(true);
                        setTotalCount(dataRows.length);
                    } else {
                        setResults(dataRows);
                        setTruncated(false);
                    }
                }
                setDownloadUrl(result.downloadUrl);
                setJobStatus('succeeded');
            } else {
                setError('Job failed. Check identifiers and try again.');
                setJobStatus('failed');
            }
        } catch (e: any) {
            setError(e.message || 'An error occurred');
            setJobStatus('failed');
        }
    };

    const handleClear = () => {
        setIdentifiers('');
        setTargetFields(DEFAULT_TARGET_FIELDS);
        setResults([]);
        setHeaders([]);
        setError('');
        setJobStatus('idle');
        setDownloadUrl('');
        setTruncated(false);
    };

    return (
        <div className="translate-page">
            <KatRow noGutters>
                <KatColumn xs={12}>
                    <div className="translate-header">
                        <h1 className="translate-title">Bulk Translate</h1>
                        <p className="translate-subtitle">
                            Translate identifiers via PDPS OpenSearch — paste IDs to look up resource fields.
                        </p>
                    </div>
                </KatColumn>
            </KatRow>

            <div className="translate-form">
                <div className="form-section">
                    <label className="form-label">Source Identifiers (one per line)</label>
                    <textarea
                        className="identifiers-input"
                        value={identifiers}
                        onChange={e => setIdentifiers(e.target.value)}
                        placeholder="amzn1.refund.ras.v1.xxx&#10;amzn1.refund.pces.yyy&#10;..."
                        rows={8}
                        disabled={jobStatus === 'uploading' || jobStatus === 'processing'}
                    />
                    <span className="input-count">
                        {identifiers.trim() ? identifiers.trim().split('\n').filter(l => l.trim()).length : 0} identifiers
                    </span>
                </div>

                <div className="form-row">
                    <div className="form-section">
                        <label className="form-label">Resource Type</label>
                        <select
                            className="form-select"
                            value={resourceType}
                            onChange={e => handleResourceTypeChange(e.target.value)}
                            disabled={jobStatus === 'uploading' || jobStatus === 'processing'}
                        >
                            {RESOURCE_TYPES.map(rt => (
                                <option key={rt} value={rt}>{rt}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-section">
                        <label className="form-label">Query Type</label>
                        <select
                            className="form-select"
                            value={queryType}
                            onChange={e => setQueryType(e.target.value)}
                            disabled={jobStatus === 'uploading' || jobStatus === 'processing'}
                        >
                            {QUERY_TYPES.map(qt => (
                                <option key={qt.value} value={qt.value}>{qt.label}</option>
                            ))}
                        </select>
                    </div>

                    {queryType === 'term' && (
                        <div className="form-section">
                            <label className="form-label">Source Field</label>
                            <select
                                className="form-select"
                                value={sourceField}
                                onChange={e => setSourceField(e.target.value)}
                                disabled={jobStatus === 'uploading' || jobStatus === 'processing'}
                            >
                                {availableFields.map(f => (
                                    <option key={f.value} value={f.value}>{f.label}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                <div className="form-section">
                    <label className="form-label">Target Fields</label>
                    <FieldMultiSelect
                        options={availableFields}
                        selected={targetFields}
                        onChange={setTargetFields}
                        disabled={jobStatus === 'uploading' || jobStatus === 'processing'}
                    />
                </div>

                <div className="form-actions">
                    <KatButton
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={jobStatus === 'uploading' || jobStatus === 'processing' || !identifiers.trim()}
                    >
                        {jobStatus === 'uploading' ? 'Uploading...' :
                         jobStatus === 'processing' ? 'Processing...' : 'Submit'}
                    </KatButton>
                    <KatButton onClick={handleClear}>Clear</KatButton>
                </div>
            </div>

            {error && <div className="translate-error">{error}</div>}

            {jobStatus === 'processing' && (
                <div className="translate-loading">
                    <div className="spinner" />
                    <span>Processing... This may take a few seconds.</span>
                </div>
            )}

            {jobStatus === 'succeeded' && results.length === 0 && (
                <div className="translate-results">
                    <p>No matching resources found.</p>
                </div>
            )}

            {jobStatus === 'succeeded' && results.length > 0 && (
                <div className="translate-results">
                    <div className="results-header">
                        <h3>Results {truncated && `(showing ${MAX_DISPLAY_ROWS} of ${totalCount})`}</h3>
                        {downloadUrl && (
                            <a href={downloadUrl} className="download-link" download>
                                Download Full CSV
                            </a>
                        )}
                    </div>
                    <div className="results-table-wrapper">
                        <table className="results-table">
                            <thead>
                                <tr>
                                    {headers.map((h, i) => <th key={i}>{h}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((row, i) => (
                                    <tr key={i}>
                                        {row.map((cell, j) => (
                                            <td key={j} title={cell}>{cell}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {truncated && (
                        <div className="truncation-notice">
                            Showing first {MAX_DISPLAY_ROWS} rows. Download the full CSV for all {totalCount} results.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

function parseCsv(csv: string): string[][] {
    return csv.trim().split(/\r?\n/).map(line => {
        const result: string[] = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') {
                if (inQuotes && line[i + 1] === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (ch === ',' && !inQuotes) {
                result.push(current);
                current = '';
            } else {
                current += ch;
            }
        }
        result.push(current);
        return result;
    });
}

export default TranslatePage;
